/**
 * «Ещё 5 новых» · окно 2 запуска, часть 2 — тексты и правила КНОПКИ добавки.
 *
 * Одиннадцатый модуль витрины с настоящей логикой. Решение принимает ядро
 * (`core/runner.grant_extra_new`): сколько слов даёт нажатие, сколько нажатий в день
 * и хватит ли уже — считает оно, а `POST /session/extra` только отдаёт результат.
 * Здесь — ровно то, что должен уметь ЭКРАН:
 *   • понять, показывать ли кнопку вообще;
 *   • написать на ней цифру ЯДРА (`extra_step`), ⛔ а не свою копию числа 5;
 *   • ⭐ ПРЕДУПРЕДИТЬ ДО НАЖАТИЯ, что завтра повторений будет больше.
 *
 * ⛔ Ни одной своей цифры и ни одного своего правила о нагрузке. Продублируй витрина
 * шаг или потолок — они разъехались бы с ядром МОЛЧА: кнопка обещала бы «ещё 5», а
 * сервер давал бы другое число (та же грабля, что уже ловили с копиями правил ядра).
 */

/** Поля сводки дня, которые нужны кнопке. Все приходят из `runner.daily_stats`. */
export interface ExtraStats {
  /** Сколько новых слов ещё разрешено сегодня (норма + надбавки − уже введённые). */
  new_left?: number;
  /** Сколько новых слов вообще созрело и ждёт (⛔ 0 = добавлять нечего). */
  due_new?: number;
  /** Сколько раз сегодня уже брали добавку. */
  extra_grants?: number;
  /** Сколько слов дали надбавки за сегодня. */
  extra_new?: number;
  /** Сколько нажатий осталось сегодня (потолок дня считает ядро). */
  extra_left?: number;
  /** Сколько новых слов даёт ОДНО нажатие. */
  extra_step?: number;
}

function num(v: number | undefined | null): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
}

/** Шаг надбавки — только из ядра. Ядро молчит (старый сервер) → кнопки нет вовсе. */
export function step(s: ExtraStats | null | undefined): number {
  return num(s?.extra_step);
}

/**
 * Показывать ли кнопку. Три условия, и каждое проверяемо:
 *   1. ядро вообще умеет надбавку (`extra_step` пришёл);
 *   2. нажатия на сегодня не кончились (`extra_left`);
 *   3. ⭐ дневная норма новых УЖЕ выбрана (`new_left === 0`) и новые слова ЕСТЬ
 *      (`due_new`). Пока норма не выбрана, добавка бессмысленна — слова и так дадут;
 *      а если новых слов не осталось в словаре, поднимать лимит нечему.
 */
export function canAsk(s: ExtraStats | null | undefined): boolean {
  if (!s || step(s) <= 0) return false;
  if (num(s.extra_left) <= 0) return false;
  return num(s.new_left) === 0 && num(s.due_new) > 0;
}

/** Надпись на кнопке: цифра — из ядра. */
export function label(s: ExtraStats | null | undefined): string {
  const n = step(s);
  return `Ещё ${n} ${plural(n, "новое слово", "новых слова", "новых слов")}`;
}

/**
 * ⭐⭐ ЧЕСТНОЕ ПРЕДУПРЕЖДЕНИЕ. Оно обязано быть видно ДО нажатия, а не после:
 * человек берёт нагрузку на завтра, и это его осознанный выбор, а не сюрприз.
 * ⛔ Это не запрет: кнопка живая.
 */
export function warning(): string {
  return "Завтра повторений будет больше — новые слова возвращаются на закрепление.";
}

/** Сколько раз ещё можно попросить сегодня. Пусто, если это последняя добавка. */
export function leftNote(s: ExtraStats | null | undefined): string {
  const left = num(s?.extra_left);
  if (left <= 1) return "";
  return `Сегодня можно попросить ещё ${left} ${plural(left, "раз", "раза", "раз")}.`;
}

/** Что показать, когда добавки на сегодня кончились (кнопки уже нет). */
export function capNote(s: ExtraStats | null | undefined): string {
  const added = num(s?.extra_new);
  if (!added) return "";
  return `Сегодня вы добавили ${added} ${plural(added, "слово", "слова", "слов")} ` +
    "сверх нормы — на сегодня хватит. Завтра норма придёт сама.";
}

/** Строка «сколько уже добавлено» для сводки подхода. Пусто, если добавок не было. */
export function takenNote(s: ExtraStats | null | undefined): string {
  const added = num(s?.extra_new);
  if (!added) return "";
  return `+${added} ${plural(added, "новое слово", "новых слова", "новых слов")} сверх нормы`;
}

/** Человеческий текст отказа. ⛔ Кода ошибки человеку не показываем (правило 26.07). */
export function refusal(status: number): string {
  if (status === 429) return "На сегодня добавок больше нет — завтра норма придёт сама.";
  return "Не получилось добавить слова. Попробуйте ещё раз.";
}
